import type { PdfViewerProps, PdfViewerSize } from "./pdf-viewer.type";
import type { PageCallback } from "react-pdf/dist/shared/types.js";

import useResizeObserver from "@react-hook/resize-observer";
import classNames from "classnames";
import { useEffect, useMemo, useRef, useState } from "react";
import { Document, Page } from "react-pdf";

import { CenteredEmpty } from "@/components/Elements/Empty";
import { CenteredSpinner } from "@/components/Elements/Spin";

import { PdfViewerClickableArea } from "./pdf-viewer-clickable-area";
import { PdfViewerToolbar } from "./pdf-viewer-toolbar";
import { ZoomDefaults } from "./pdf-viewer.constant";
import {
  getPdfDocumentOptions,
  getPdfFitType,
  getPdfPageSize,
} from "./pdf-viewer.helper";

import "./pdf-viewer.style.less";

import "react-pdf/dist/Page/AnnotationLayer.css";
import "react-pdf/dist/Page/TextLayer.css";

export const PdfViewer = ({
  source,
  initialPage = 1,
  controlledPage,
  highlightPage = 0,
  toolbarOptions,
  onPageChange,
  onPageSizeChange,
  onPdfClick,
  fitToWrapper,
  className,
  testId,
  children,
}: PdfViewerProps) => {
  const wrapperRef = useRef<HTMLDivElement>(null);

  const [loaded, setLoaded] = useState(false);
  const [zoom, setZoom] = useState(1);
  const [pagesCount, setPagesCount] = useState(0);
  const [page, setPage] = useState(controlledPage ?? initialPage);

  const [wrapperSize, setWrapperSize] = useState<PdfViewerSize>();
  const [containerSize, setContainerSize] = useState<PdfViewerSize>();
  const [pdfPageRatio, setPdfPageRatio] = useState<number | undefined>(
    undefined
  );

  const fitType = getPdfFitType({ fitToWrapper, wrapperSize, pdfPageRatio });
  const pageSize = getPdfPageSize({ fitType, wrapperSize });
  const pdfDocumentOptions = useMemo(() => getPdfDocumentOptions(), []);

  const handlePageChange = (newPage: number) => {
    setPage(newPage);
    onPageChange?.(newPage);
  };

  const onPreviousPage = () => handlePageChange(page - 1);
  const onNextPage = () => handlePageChange(page + 1);
  const onGoToHighlightPage = () => handlePageChange(highlightPage);

  const onZoomIncrease = () => setZoom((zoom) => zoom + ZoomDefaults.increment);
  const onZoomDecrease = () => setZoom((zoom) => zoom - ZoomDefaults.increment);

  const onDocumentLoadSuccess = ({ numPages }: { numPages: number }) =>
    setPagesCount(numPages);

  const onPageRenderSuccess = (page: PageCallback) => {
    const { originalWidth, originalHeight } = page;
    const pdfRatio = originalWidth / originalHeight;

    if (pdfRatio !== pdfPageRatio) setPdfPageRatio(pdfRatio);

    if (!loaded) setLoaded(true);
  };

  const onAreaSizeChange = (areaSize: PdfViewerSize) => {
    // Define `containerSize` using `areaSize` to avoid container's scrollbar
    // width and height calculation mistakes.
    setContainerSize(areaSize);
  };

  const {
    showPagination = false,
    showZoom = false,
    showGoToHighlightPage = false,
  } = toolbarOptions ?? {};

  const showToolbar = (showPagination || showZoom) && pagesCount > 0 && loaded;

  useResizeObserver(wrapperRef, (wrapper) => {
    const { width, height } = wrapper.contentRect;
    setWrapperSize({ width, height });
  });

  useEffect(() => {
    if (!loaded || !containerSize) return;
    onPageSizeChange?.(containerSize);
  }, [loaded, containerSize]);

  useEffect(() => {
    if (controlledPage) setPage(controlledPage);
  }, [controlledPage]);

  return (
    <div
      className={classNames("pdf-viewer", className, {
        "-hide-toolbar": !showToolbar, // hide tool bar in CSS to make sur document & page loader are display at the same place
      })}
      data-testid={testId}
    >
      <PdfViewerToolbar
        page={page}
        showPagination={showPagination}
        pagesCount={pagesCount}
        onPreviousPage={onPreviousPage}
        onNextPage={onNextPage}
        zoom={zoom}
        showZoom={showZoom}
        onZoomDecrease={onZoomDecrease}
        onZoomIncrease={onZoomIncrease}
        zoomMin={ZoomDefaults.min}
        zoomMax={ZoomDefaults.max}
        showGoToHighlightPage={showGoToHighlightPage}
        disableHighlightPage={!highlightPage || highlightPage === page}
        onGoToHighlightPage={onGoToHighlightPage}
      />

      <div
        ref={wrapperRef}
        className={classNames("pdf-viewer-wrapper", {
          "-fit-mode": fitType !== null,
        })}
      >
        <div className="pdf-viewer-container">
          <Document
            file={source}
            options={pdfDocumentOptions}
            onLoadSuccess={onDocumentLoadSuccess}
            loading={() => <CenteredSpinner data-testid="pdf-loading" />}
            noData={() => <CenteredEmpty data-testid="pdf-no-data" />}
          >
            <Page
              pageNumber={page}
              scale={zoom}
              width={pageSize.width}
              height={pageSize.height}
              onRenderSuccess={onPageRenderSuccess}
            >
              <PdfViewerClickableArea
                page={page}
                onAreaSizeChange={onAreaSizeChange}
                onPdfClick={onPdfClick}
              />
              {children}
            </Page>
          </Document>
        </div>
      </div>
    </div>
  );
};
